import React from 'react';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import Tooltip from '@amzn/meridian/tooltip';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import TranscriptDisplay, { TranscriptDisplayProps } from '../session/TranscriptDisplay';
import styles from './ConsentWindowComponent.module.scss';

export const DEFAULT_CONSENT_SCRIPT =
  'Before we begin, I want to let you know I will be using a tool to help me take audio notes. ' +
  'You can ask me to stop using the tool at any point. Do I have your consent to continue?';

const CONSENT_SCRIPT_TITLE = 'Consent script';

/** Placeholder wording — swap for the approved copy once it is provided. */
const CONSENT_SCRIPT_HELP =
  'Read this script aloud to the associate before the interview. The consent agent listens to '
  + 'their reply and records the decision for you, so consent is never set by hand.';

const CONSENT_HELP_TOOLTIP_MAX_WIDTH = 280;

/** Rolling cap for the consent-window transcript: only the most recent lines are shown
 *  (older ones roll off the top), enough for the consent exchange without the rest of the
 *  interview surfacing before the consent agent decides. Paired with the `.transcriptBox`
 *  height, which bounds the box to roughly this many lines. */
export const CONSENT_TRANSCRIPT_MAX_LINES = 4;

interface ConsentWindowComponentProps {
  transcriptionDisplay: TranscriptDisplayProps
  consentScript?: string;
}
const ConsentWindowComponent: React.FC<ConsentWindowComponentProps> = ({
  transcriptionDisplay,
  consentScript = DEFAULT_CONSENT_SCRIPT,
}) => (
  <div className={styles.container}>
    <Column alignmentHorizontal="center" spacing="400" width="100%">
      {/* Consent script the associate hears read aloud. */}
      <div className={styles.panel}>
        <Tooltip
          position="end"
          title={CONSENT_SCRIPT_HELP}
          maxWidth={CONSENT_HELP_TOOLTIP_MAX_WIDTH}
        >
          <Icon className={styles.icon} tokens={infoTokens} tabIndex={0}>About the consent script</Icon>
        </Tooltip>
        <div className={styles.content}>
          <Text type="h100">{CONSENT_SCRIPT_TITLE}</Text>
          <div>
            <Text type="b400">&ldquo;{consentScript}&rdquo;</Text>
          </div>
          <div className={styles.transcriptBox}>
            <TranscriptDisplay {...transcriptionDisplay} maxLines={CONSENT_TRANSCRIPT_MAX_LINES}/>
          </div>
          <div>
            <Text type="b200" color="secondary">Transcript limited to the consent exchange until consent is confirmed.</Text>
          </div>
        </div>
      </div>
    </Column>
  </div>
);

export default ConsentWindowComponent;
